import { useState, useEffect, useCallback } from 'react'
import { History, RotateCcw, Trash2, X } from 'lucide-react'

const STORAGE_KEY = 'visual_prompt_history'
const MAX_ENTRIES = 20

export interface PromptHistoryEntry {
  id: string
  prompt: string
  canal: string
  preset: string
  usedAt: string
}

/** Read history from localStorage, returning an empty array on parse errors. */
function readHistory(): PromptHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    return JSON.parse(raw) as PromptHistoryEntry[]
  } catch {
    return []
  }
}

/** Save history to localStorage. */
function writeHistory(entries: PromptHistoryEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // Ignore quota errors silently
  }
}

/** Public function to push a new prompt to history. Call from VisualTaskForm on submit. */
export function pushPromptHistory(entry: Omit<PromptHistoryEntry, 'id' | 'usedAt'>): void {
  const history = readHistory()
  // De-duplicate: remove existing entry with same prompt text
  const filtered = history.filter((e) => e.prompt !== entry.prompt)
  const newEntry: PromptHistoryEntry = {
    ...entry,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    usedAt: new Date().toISOString(),
  }
  const updated = [newEntry, ...filtered].slice(0, MAX_ENTRIES)
  writeHistory(updated)
}

interface PromptHistoryPanelProps {
  /** Called when the user selects a prompt to reuse */
  onSelect: (entry: PromptHistoryEntry) => void
  /** When true, renders as a collapsible inline panel; otherwise a positioned overlay */
  inline?: boolean
  className?: string
}

export function PromptHistoryPanel({ onSelect, inline = false, className = '' }: PromptHistoryPanelProps) {
  const [history, setHistory] = useState<PromptHistoryEntry[]>([])
  const [isOpen, setIsOpen] = useState(false)

  const reload = useCallback(() => setHistory(readHistory()), [])

  useEffect(() => {
    reload()
  }, [reload])

  useEffect(() => {
    if (isOpen) reload()
  }, [isOpen, reload])

  const handleDelete = (id: string) => {
    const updated = history.filter((e) => e.id !== id)
    writeHistory(updated)
    setHistory(updated)
  }

  const handleClear = () => {
    if (!window.confirm('¿Eliminar todo el historial de prompts?')) return
    writeHistory([])
    setHistory([])
  }

  const handleSelect = (entry: PromptHistoryEntry) => {
    onSelect(entry)
    if (!inline) setIsOpen(false)
  }

  const trigger = (
    <button
      type="button"
      onClick={() => { reload(); setIsOpen((v) => !v) }}
      className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-600 font-medium transition-colors"
      title="Historial de prompts"
    >
      <History className="w-4 h-4" />
      Historial
      {history.length > 0 && (
        <span className="ml-1 inline-flex items-center justify-center rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold w-4 h-4">
          {history.length}
        </span>
      )}
    </button>
  )

  const content = isOpen || inline ? (
    <div
      className={`
        bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden
        ${inline ? '' : 'absolute right-0 top-full mt-1 z-30 w-96 max-w-[calc(100vw-2rem)]'}
      `}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          Ultimos {MAX_ENTRIES} prompts usados
        </span>
        <div className="flex items-center gap-1">
          {history.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              title="Borrar historial"
              className="p-1 rounded hover:bg-red-50 hover:text-red-600 text-slate-400 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {!inline && (
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-slate-200 text-slate-400 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {history.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-slate-400">
          Sin prompts en el historial aun
        </div>
      ) : (
        <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100">
          {history.map((entry) => (
            <li key={entry.id} className="group flex items-start gap-2 px-3 py-2.5 hover:bg-slate-50">
              <button
                type="button"
                title="Reusar este prompt"
                onClick={() => handleSelect(entry)}
                className="flex-1 text-left min-w-0"
              >
                <p className="text-sm text-slate-700 line-clamp-2 leading-snug">
                  {entry.prompt}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-slate-400">
                    {new Date(entry.usedAt).toLocaleDateString('es', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {entry.canal && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">
                      {entry.canal}
                    </span>
                  )}
                  {entry.preset && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">
                      {entry.preset}
                    </span>
                  )}
                </div>
              </button>
              <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => handleSelect(entry)}
                  title="Reusar"
                  className="p-1 rounded hover:bg-emerald-50 hover:text-emerald-600 text-slate-400 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(entry.id)}
                  title="Eliminar"
                  className="p-1 rounded hover:bg-red-50 hover:text-red-600 text-slate-400 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : null

  if (inline) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" />
            Historial de prompts
          </span>
          {history.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-slate-400 hover:text-red-500 transition-colors"
            >
              Limpiar
            </button>
          )}
        </div>
        {content}
      </div>
    )
  }

  return (
    <div className={`relative ${className}`}>
      {trigger}
      {content}
    </div>
  )
}
